import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { defineCustomElements, addTheme } from '@scania/components';
import { theme as scania } from '@scania/theme-light';
import { AuthService } from './services/auth.service';
import { OrganizationalGroupsActions } from './store/organizationalGroups/organizationalGroups.actions';
import { Store } from '@ngxs/store';
import { Observable, combineLatest, of } from 'rxjs';
import { ActivatedRoute, NavigationEnd, ParamMap, Router } from '@angular/router';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FpcsActions } from './store/fpcs/fpcs.actions';
import { CheckedOutItemService } from './services/checked-out-item.service';
import { MessageService } from 'primeng/api';
import { ItemType } from './models/enums/itemType';
import { LoaderService } from './services/loader.service';
import { TestCasesActions } from './store/testCases/testCases.actions';
import { TestAnalysisActions } from './store/testAnalysis/testAnalysis.actions';
import { TemplatesActions } from './store/templates/templates.actions';
import { LoadingService } from './services/loading.serivce';
import { environment } from 'src/environments/environment';
import { ApolloClientService } from './services/api/apollo-client.service';
import { GraphQLUserDataOutput } from './graphQL/models/output/graphql-user-data-output';
import { CONSUME_ONE_TIME_TOKEN } from './graphQL/mutations/authentication-mutations';
import { convertGraphqlUserDataOutputToUserData } from './graphQL/mappers/user-data.mappers';
import { ThemeService } from './services/theme.service';
import { HeaderComponent } from './components/header/header.component';
import { SpinnerComponent } from './components/common/spinner/spinner.component';
import { HeaderHeightDirective } from './directives/header-height.directive';
import { BrowserAnalyticsService } from './services/browser-analytics.service';
import { UpdateService } from './services/update.service';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

defineCustomElements();
addTheme(scania);

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        RouterOutlet,
        ToastModule,
        ButtonModule,
        CardModule,
        ProgressSpinnerModule,
        HeaderComponent,
        SpinnerComponent,
        HeaderHeightDirective
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [MessageService]
})
export class AppComponent implements OnInit {
  isLoggedIn = false
  isProcessingAuthentication = signal<boolean>(true);

  private destroyRef = inject(DestroyRef);
  protected updateService = inject(UpdateService);
  params$: Observable<ParamMap> = this.route.queryParamMap;
  navigationEnded$ = this.router.events.pipe(filter(e => e instanceof NavigationEnd));
  isLoading$ = this.loadingService.loading$;
  isLoadingBackground$ = this.loadingService.loadingInBackground$

  constructor(
    public loaderService: LoaderService,
    public authService: AuthService,
    private checkedOutItemService: CheckedOutItemService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private loadingService: LoadingService,
    private store: Store,
    private apolloClientService: ApolloClientService,
    private themeService: ThemeService,
    private browserAnalyticsService: BrowserAnalyticsService) {}

  ngOnInit(): void {
    this.themeService.getTheme();

    this.browserAnalyticsService.start(this.router, this.destroyRef);

    // Show a reload notice when a newer version has been deployed. The user decides when to reload.
    this.updateService.updateAvailable$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.messageService.add({
          key: 'reload',
          severity: 'info',
          summary: 'Update available',
          detail: 'A new version of Configurator is available. Save your work, then reload to get the new version.',
          sticky: true,
        }),
      );

    combineLatest({
      params: this.params$,
      navigationEnded: this.navigationEnded$,
      userData: this.authService.userData$,
    }).pipe(
      distinctUntilChanged((prev, curr) => prev.userData.sessionUID === curr.userData.sessionUID),
      tap(() => this.isProcessingAuthentication.set(true)),
      switchMap(data => {
        if (!data.userData.sessionUID) {
          const token = this.authService.getQueryParam(environment.oneTimeTokenName);
          if (token) {
            if (token === 'noAuth') {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to sign in. No authentication token provided..' });
              return of(false);
            } else {
              return this.apolloClientService.authLayerClient.pipe(
                take(1),
                switchMap(client =>
                  client.mutate<{ consumeOneTimeToken: GraphQLUserDataOutput }>({
                    mutation: CONSUME_ONE_TIME_TOKEN,
                    variables: { token: token },
                  }),
                ),
                map(result => {
                  const mutationResult = result.data?.consumeOneTimeToken
                  return (mutationResult ? convertGraphqlUserDataOutputToUserData(mutationResult) : undefined)
                }),
                map(data => {
                  if (data?.sessionUID) {
                    this.authService.updateSession(data.sessionUID, data.userName, data.companyName, data.functionalGroups, data.firstName, data.lastName);
                    return false;
                  } else {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to retrieve user data. Please sign in again.' });
                    return false;
                  }
                }),
              );
            }
          } else {
            return of(true);
          }
        }
        return of(false);
      }),
      tap(shouldAuthenticate => {
        if (shouldAuthenticate && !this.authService.autoLoginSuppressed) {
          this.authService.login();
        }
      }),
      tap(() => this.authService.removeSpecificQueryParams(['sessionId', 'username', 'company', 'functionalGroups', environment.oneTimeTokenName])),
      tap(() => this.isProcessingAuthentication.set(false)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    this.authService.userData$.subscribe((result) => {
      this.isLoggedIn = result.isLoggedIn;
      //this.store.dispatch(new TestCasesActions.SetAllTestCasesPaginated(result.companyName));
      this.store.dispatch(new TestAnalysisActions.SetAllTestAnalysis(result.companyName));

      if(this.isLoggedIn)
        this.store.dispatch(new OrganizationalGroupsActions.SetOrganizationalGroupByUsername(result.userName));
    });

    this.store.dispatch(new TemplatesActions.SetTASearchTemplate);
    this.store.dispatch(new TemplatesActions.SetTCSearchTemplate);
    //load fpc right away because it takes a while to load them
    this.store.dispatch(new FpcsActions.SetAllFpcs());

    this.checkTypeOfCheckedOutItem();
  }

  private checkTypeOfCheckedOutItem() {
    //check only once, not on every check in/out
    this.checkedOutItemService.checkedItemUidChange.pipe(take(1)).subscribe(itemUid => {
      if(this.checkedOutItemService.exists() && this.checkedOutItemService.getCheckedOutItemType() == ItemType.Unsupported)
        this.messageService.add({ severity: 'info', summary: 'You have a checked out item in Configurator Desktop app and will not be able to edit items in Fenix', sticky: true});
    });
  }
}
